<template>
  <div class="app-container">
    <div class="header-section">
      <h2 class="page-title">Prośby o dostęp do MLS</h2>
      <p class="page-subtitle">Akceptuj lub odrzucaj prośby firm o dołączenie do Giełdy Współpracy.</p>
    </div>

    <!-- Filter -->
    <el-card class="filter-card">
      <div class="filter-row">
        <el-radio-group v-model="filterStatus" @change="loadRequests">
          <el-radio-button label="pending">Oczekujące <el-badge v-if="stats.pending > 0" :value="stats.pending" /></el-radio-button>
          <el-radio-button label="">Wszystkie</el-radio-button>
          <el-radio-button label="approved">Zaakceptowane</el-radio-button>
          <el-radio-button label="rejected">Odrzucone</el-radio-button>
        </el-radio-group>
      </div>
    </el-card>

    <!-- Requests Table -->
    <el-card class="requests-card">
      <el-table v-loading="loading" :data="requests" border style="width: 100%">
        <el-table-column label="Firma" min-width="200">
          <template slot-scope="scope">
            <div class="user-cell">
              <div class="user-name">{{ scope.row.company ? scope.row.company.name : '—' }}</div>
              <div v-if="scope.row.company" class="user-email">ID: {{ scope.row.company.id }}</div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Użytkownik (właściciel)" min-width="220">
          <template slot-scope="scope">
            <div v-if="scope.row.user" class="user-cell">
              <div class="user-name">{{ scope.row.user.name }}</div>
              <div class="user-email">{{ scope.row.user.email }}</div>
            </div>
            <span v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column label="Uzasadnienie" min-width="240">
          <template slot-scope="scope">
            <div v-if="scope.row.notes" class="notes-cell">{{ scope.row.notes }}</div>
            <span v-else class="text-gray">—</span>
          </template>
        </el-table-column>

        <el-table-column label="Status" width="140" align="center">
          <template slot-scope="scope">
            <el-tag :type="statusTagType(scope.row.status)" size="small">
              {{ statusLabel(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Data zgłoszenia" width="150" align="center">
          <template slot-scope="scope">
            {{ formatDate(scope.row.created_at) }}
          </template>
        </el-table-column>

        <el-table-column label="Decyzja" width="180">
          <template slot-scope="scope">
            <div v-if="scope.row.approved_at" class="user-cell">
              <div class="user-name" v-if="scope.row.approver">{{ scope.row.approver.name }}</div>
              <div class="user-email">{{ formatDate(scope.row.approved_at) }}</div>
              <div v-if="scope.row.rejection_reason" class="rejection-reason">
                <em>{{ scope.row.rejection_reason }}</em>
              </div>
            </div>
            <span v-else class="text-gray">—</span>
          </template>
        </el-table-column>

        <el-table-column label="Akcje" width="220" align="center" fixed="right">
          <template slot-scope="scope">
            <div v-if="scope.row.status === 'pending'">
              <el-button type="success" size="mini" icon="el-icon-check"
                         @click="handleApprove(scope.row)" :loading="actingId === scope.row.id">
                Zaakceptuj
              </el-button>
              <el-button type="danger" size="mini" icon="el-icon-close"
                         @click="openRejectDialog(scope.row)" :disabled="actingId === scope.row.id">
                Odrzuć
              </el-button>
            </div>
            <span v-else class="text-gray">—</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrapper" v-if="total > pageSize">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page.sync="currentPage"
          @current-change="loadRequests"
        />
      </div>
    </el-card>

    <!-- Reject dialog -->
    <el-dialog title="Odrzuć prośbę o dostęp" :visible.sync="rejectDialog.visible" width="500px">
      <p>Podaj powód odrzucenia. Zostanie wysłany do użytkownika w mailu.</p>
      <el-input
        v-model="rejectDialog.reason"
        type="textarea"
        :rows="4"
        placeholder="Np. niekompletne dane firmy / brak weryfikacji REGON / itp."
      />
      <span slot="footer">
        <el-button @click="rejectDialog.visible = false">Anuluj</el-button>
        <el-button type="danger" @click="confirmReject" :loading="rejectDialog.submitting"
                   :disabled="!rejectDialog.reason || !rejectDialog.reason.trim()">
          Odrzuć prośbę
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import MlsAccessRequestsResource from '@/api/mlsAccessRequests';

const api = new MlsAccessRequestsResource();

export default {
  name: 'MlsAccessRequests',
  data() {
    return {
      loading: false,
      requests: [],
      total: 0,
      pageSize: 20,
      currentPage: 1,
      filterStatus: 'pending',
      actingId: null,
      stats: { pending: 0 },
      rejectDialog: {
        visible: false,
        request: null,
        reason: '',
        submitting: false,
      },
    };
  },
  mounted() {
    this.loadRequests();
    this.loadPendingCount();
  },
  methods: {
    async loadRequests() {
      this.loading = true;
      try {
        const res = await api.getRequests({
          status: this.filterStatus || undefined,
          page: this.currentPage,
          per_page: this.pageSize,
        });
        const payload = res.data && res.data.data !== undefined ? res.data : res;
        this.requests = payload.data || [];
        this.total = payload.total || 0;
      } catch (e) {
        this.$message.error('Nie udało się załadować próśb');
      } finally {
        this.loading = false;
      }
    },
    async loadPendingCount() {
      try {
        const res = await api.getPendingCount();
        const payload = res.data && res.data.count !== undefined ? res.data : res;
        this.stats.pending = payload.count || 0;
      } catch (e) { /* ignore */ }
    },
    async handleApprove(row) {
      try {
        await this.$confirm(
          `Zaakceptować prośbę firmy "${row.company ? row.company.name : '—'}"? Spowoduje to aktywację członkostwa MLS.`,
          'Potwierdź akceptację',
          { confirmButtonText: 'Tak, zaakceptuj', cancelButtonText: 'Anuluj', type: 'success' }
        );
      } catch (e) {
        return; // cancelled
      }

      this.actingId = row.id;
      try {
        await api.approve(row.id);
        this.$message.success('Prośba zaakceptowana — użytkownik dostanie email.');
        this.loadRequests();
        this.loadPendingCount();
      } catch (e) {
        this.$message.error('Nie udało się zaakceptować');
      } finally {
        this.actingId = null;
      }
    },
    openRejectDialog(row) {
      this.rejectDialog.visible = true;
      this.rejectDialog.request = row;
      this.rejectDialog.reason = '';
    },
    async confirmReject() {
      const reason = (this.rejectDialog.reason || '').trim();
      if (!reason) return;
      this.rejectDialog.submitting = true;
      try {
        await api.reject(this.rejectDialog.request.id, reason);
        this.$message.success('Prośba odrzucona — użytkownik dostanie email z powodem.');
        this.rejectDialog.visible = false;
        this.loadRequests();
        this.loadPendingCount();
      } catch (e) {
        this.$message.error('Nie udało się odrzucić');
      } finally {
        this.rejectDialog.submitting = false;
      }
    },
    statusLabel(s) {
      return { pending: 'Oczekuje', approved: 'Zaakceptowana', rejected: 'Odrzucona' }[s] || s;
    },
    statusTagType(s) {
      return { pending: 'warning', approved: 'success', rejected: 'danger' }[s] || 'info';
    },
    formatDate(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      return d.toLocaleDateString('pl-PL') + ' ' + d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>

<style lang="scss" scoped>
.app-container { padding: 20px; }
.header-section { margin-bottom: 24px; }
.page-title { margin: 0 0 4px; font-size: 22px; font-weight: 600; }
.page-subtitle { margin: 0; color: #909399; font-size: 14px; }
.filter-card { margin-bottom: 16px; }
.filter-row { display: flex; gap: 12px; align-items: center; }
.requests-card { margin-bottom: 16px; }
.user-cell { line-height: 1.4; }
.user-name { font-weight: 500; color: #303133; }
.user-email { font-size: 12px; color: #909399; }
.notes-cell {
  font-size: 13px; color: #606266;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
  overflow: hidden; max-height: 60px;
}
.rejection-reason {
  margin-top: 4px; font-size: 11px; color: #f56c6c;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.text-gray { color: #c0c4cc; }
.pagination-wrapper { margin-top: 16px; text-align: center; }
</style>
